import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Calendar, Clock, Heart, Loader, MapPin, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import authAxios from "../../authAxios";
import instance from "../../axios";
import Toast from "../../components/common/Toast";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader } from "../../intro/components/landing-new";
import { uploadBigFileToS3 } from "../../services/uploads";
import FriendFormModal from "./components/FriendFormModal";
import InfoToast from "./components/InfoToast";
import ProfileDetailModal from "./components/ProfileDetailModal";
import { useTranslation } from "react-i18next";
import { isInApp } from "../../utils/appShell";

const SinglesInferno = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const profileRef = useRef(null);
  const formRef = useRef(null);
  const [votedIdeas, setVotedIdeas] = useState(new Set());
  const [minVotes, setMinVotes] = useState(4);
  const [newIdea, setNewIdea] = useState({
    name: "",
    description: "",
    telegram: "",
    images: [],
    imagePreviews: [],
  });

  const [user] = useGlobalState("user");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const search = urlParams.get("search");

    if (success === "true") {
      showToast(
        t(
          "We've listed your friend! Share this page link so we can all vote for your friend to enter Love Island <3"
        ),
        "celebration"
      );
    }

    if (search) {
      setSearchQuery(search);
    }
  }, [t]);

  useEffect(() => {
    fetchIdeas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setShowForm(false);
      }
    };

    if (showForm) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showForm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setSelectedIdea(null);
      }
    };

    if (selectedIdea) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedIdea]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewIdea((prev) => ({
          ...prev,
          images: [...prev.images, file],
          imagePreviews: [...prev.imagePreviews, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setNewIdea((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imagePreviews: prev.imagePreviews.filter((_, i) => i !== index),
    }));
  };

  const fetchIdeas = async () => {
    try {
      const response = await instance.get("/v1/singlesinferno", {
        params: {
          userId: user.id,
        },
      });
      const profiles = response.data;
      setIdeas(profiles);

      // 按投票數降序排列個人資料並獲取第30名（如果存在）
      const sortedProfiles = [...profiles].sort((a, b) => b.votes - a.votes);
      const minPerson = sortedProfiles.length >= 30 ? sortedProfiles[29] : null;

      if (minPerson) {
        setMinVotes(minPerson.votes);
      }

      if (response.data.length > 0 && selectedIdea) {
        setSelectedIdea(response.data.find((idea) => idea.id === selectedIdea.id));
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const getVotedIdeas = async () => {
    try {
      const response = await authAxios.get("/v1/singlesinferno/getVotedIdeas");
      setVotedIdeas(new Set(response.data.map((idea) => idea.singleinfernos_id)));
    } catch (error) {
      console.error("Error fetching voted ideas data:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      getVotedIdeas();
    }
  }, [user?.id]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleCheckout = async (uploaded_id, friend_name) => {
    // 呼叫後端創建會話
    const response = await instance.post("/v1/stripe/createCheckoutSession", {
      uploaded_id,
      friend_name,
    });

    const { url } = await response.data;

    // 重定向到Stripe結帳頁面
    // stripe.redirectToCheckout({ sessionId: url });
    window.location.href = url;
  };

  const handleVote = async (id) => {
    // const now = dayjs();
    // const votingOpens = dayjs("2025-02-16T12:00:00+08:00");

    // if (now.isBefore(votingOpens)) {
    //   showToast("投票將於2月16日中午12點開始！", "error");
    //   return;
    // }

    if (votedIdeas.has(id)) {
      return; // 如果已經投票則提前返回
    }

    try {
      setVotedIdeas((prev) => new Set([...prev, id])); // 添加到已投票集合
      await authAxios.post(`/v1/singlesinferno/${id}/vote`, {
        increment: true,
        userId: user.id,
      });
      await fetchIdeas();
      showToast("Wow! Look at you, Cupid!", "success");
    } catch (error) {
      showToast(error.response.data.error || "Voting failed. Please try again.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    if (newIdea.images.length === 0) {
      showToast(t("Please upload at least one photo"), "error");
      setLoadingSubmit(false);
      return;
    }

    // Validate required fields
    if (!newIdea.name?.trim()) {
      showToast(t("Please enter your friend's name"), "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.description?.trim()) {
      showToast(t("Please tell us more about your friend"), "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.telegram?.trim()) {
      showToast(t("Please provide your friend's contact"), "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.gender) {
      showToast(t("Please select your friend's gender"), "error");
      setLoadingSubmit(false);
      return;
    }

    try {
      // Upload all images first
      const uploadedImageUrls = await Promise.all(
        newIdea.images.map((image) => uploadBigFileToS3(image))
      );

      const profileData = {
        name: newIdea.name,
        description: newIdea.description,
        telegram: newIdea.telegram,
        gender: newIdea.gender,
        images: uploadedImageUrls,
        payment_intent: "tw_bali_inferno_2025",
      };

      if (user.id) {
        profileData.userId = user.id;
      }

      const uploadedProfile = await authAxios.post("/v1/singlesinferno", profileData);

      // handleCheckout(uploadedProfile?.data?.id, newIdea.name);

      setShowForm(false);
      // setNewIdea({
      //   name: "",
      //   description: "",
      //   telegram: "",
      //   category: "",
      //   images: [],
      //   imagePreviews: [],
      // });

      showToast(
        t(
          "We've received your nomination! Share this page link so we can all vote for your friend to enter Love Island <3"
        ),
        "celebration"
      );

      await fetchIdeas();
    } catch (error) {
      console.error("Error submitting data:", error);
      showToast(t("Failed to recommend your friend. Error: ") + error, "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const filteredIdeas = ideas
    .filter(
      (idea) =>
        (idea.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          idea.name.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (!selectedCategory || idea.gender === selectedCategory)
    )
    .sort((a, b) => {
      if (selectedCategory !== "") {
        return b.votes - a.votes;
      }
      return 0;
    });

  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-px-4 tw-pb-10 tw-pt-24 sm:tw-px-6 lg:tw-px-8">
      <Dialog
        open={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-2xl tw-font-bold">{t("singles_inferno.how_it_works")}</span>
            <button
              onClick={() => setShowHowItWorks(false)}
              className="tw-rounded-full tw-p-1 hover:tw-bg-gray-100"
            >
              <X className="tw-h-6 tw-w-6 tw-text-gray-500" />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="tw-space-y-6 tw-py-4">
            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                1
              </div>
              <div>
                <p className="tw-text-gray-600">
                  {t("singles_inferno.recommend_friend_description")}
                </p>
              </div>
            </div>

            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                2
              </div>
              <div>
                <p className="tw-text-gray-600">{t("singles_inferno.how_it_works_2")}</p>
              </div>
            </div>

            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                3
              </div>
              <div>
                <p className="tw-text-gray-600">{t("singles_inferno.how_it_works_3")}</p>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                4
              </div>
              <div>
                <p className="tw-text-gray-600">{t("singles_inferno.how_it_works_4")}</p>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                5
              </div>
              <div>
                <p className="tw-text-gray-600">{t("singles_inferno.how_it_works_5")}</p>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                6
              </div>
              <div>
                <p className="tw-text-gray-600">{t("singles_inferno.how_it_works_6")}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StickyHeader withoutCta />
      <div className="tw-mx-auto tw-min-h-[100vh] tw-max-w-4xl">
        <div className="tw-text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="tw-mb-4 tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-bg-clip-text tw-text-4xl tw-font-bold tw-text-transparent"
          >
            {t("singles_inferno.title", "Sotravel的煉獄 🏖")}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="tw-mb-8 tw-flex tw-flex-col tw-items-center tw-space-y-2"
          >
            <div className="tw-space-x- tw-flex tw-items-center">
              <Clock className="tw-mr-3 tw-h-5 tw-w-5 tw-text-gray-500" />
              <p className="tw-text-gray-600">
                {t("singles_inferno.registration_deadline", "報名截止於")}{" "}
                <span className="tw-font-semibold">
                  {t("singles_inferno.register_deadline_date", "6月7日23:59")}
                </span>
              </p>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-2">
              <Heart className="tw-h-5 tw-w-5 tw-text-rose-500" />
              <p className="tw-text-gray-600">
                {t("singles_inferno.voting_ends", "投票結束於")}{" "}
                <span className="tw-font-semibold">
                  {t("singles_inferno.voting_ends_date", "6月8日23:59")}
                </span>
              </p>
            </div>
          </motion.div>

          <div className="tw-relative tw-mx-auto tw-mb-8 tw-w-full tw-max-w-4xl">
            <img
              src="https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/243006120250527-212918.png"
              alt="Singles Inferno"
              className="tw-h-[300px] tw-w-full tw-rounded-2xl tw-object-cover tw-shadow-lg md:tw-h-[670px]"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="tw-mx-auto tw-mb-12 tw-max-w-2xl tw-text-center"
          >
            <h2 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-gray-900">
              {t("singles_inferno.about_event", "關於活動")}
            </h2>
            <div className="tw-mb-4 tw-flex tw-items-center tw-justify-center tw-space-x-2">
              <Calendar className="tw-h-5 tw-w-5 tw-text-gray-500" />
              <p className="tw-text-gray-600">
                {t("singles_inferno.event_date", "活動日期")}{" "}
                <span className="tw-font-semibold">
                  {t("singles_inferno.event_date_range", "8月15-18日")}
                </span>
              </p>
            </div>
            <div className="tw-space-y-4 tw-text-gray-600">
              <p>
                {t(
                  "singles_inferno.event_description",
                  "搭上我們的愛情快艇，前往峇里島展開四天三夜的浪漫冒險。從追海豚到跳瀑布，遊戲瘋、笑聲響、愛情火花啪啦啪啦炸！"
                )}
              </p>
              <p className="tw-font-medium tw-italic tw-text-pink-500">
                {t("singles_inferno.warning", "小心：你可能會濕身 💦")}
              </p>
            </div>
          </motion.div>
          <div className="tw-mb-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
            <div className="tw-flex tw-flex-row tw-gap-4">
              <button
                onClick={() => {
                  if (!localStorage.getItem("USER_ID")) {
                    window.location.href = `${window.location.origin}/login?reroute=/inferno`;
                    return;
                  }
                  setShowForm(!showForm);
                }}
                className="tw-rounded-full tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-px-8 tw-py-4 tw-font-semibold tw-text-white tw-shadow-lg tw-transition-all hover:tw-shadow-xl"
              >
                {t("singles_inferno.recommend_friend", "推薦朋友")}
              </button>

              <button
                onClick={() => {
                  // The app's webview drops new windows: open it in place there.
                  if (isInApp()) navigate("/trips/271");
                  else window.open(`${window.location.origin}/trips/271`, "_blank");
                }}
                className="tw-rounded-full tw-bg-gradient-to-r tw-from-[#00bfff] tw-to-[#4682B4] tw-px-8 tw-py-4 tw-font-semibold tw-text-white tw-shadow-lg tw-transition-all hover:tw-shadow-xl"
              >
                {t("singles_inferno.trip_details", "行程詳情")}
              </button>
            </div>
            <button
              onClick={() => setShowHowItWorks(true)}
              className="tw-text-gray-600 tw-underline tw-transition-colors hover:tw-text-gray-900"
            >
              {t("singles_inferno.how_it_works", "活動說明")}
            </button>
          </div>

          <div className="tw-mb-7 tw-mt-14 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <button
              onClick={() => setSelectedCategory("")}
              className={`tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                !selectedCategory
                  ? "tw-bg-[#00bfff] tw-text-white"
                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
              }`}
            >
              <span className="tw-mr-[7px]">💃</span>

              <span>{t("singles_inferno.latest_submissions", "最新列出的單身者")}</span>
            </button>
            <button
              onClick={() => setSelectedCategory("male")}
              className={`tw-flex tw-items-center tw-space-x-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                selectedCategory === "male"
                  ? "tw-bg-[#00bfff] tw-text-white"
                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
              }`}
            >
              <span>👑</span>
              <span>{t("singles_inferno.males", "男生")}</span>
            </button>
            <button
              onClick={() => setSelectedCategory("female")}
              className={`tw-flex tw-items-center tw-space-x-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                selectedCategory === "female"
                  ? "tw-bg-[#00bfff] tw-text-white"
                  : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
              }`}
            >
              <span>👸</span>
              <span>{t("singles_inferno.females", "女生")}</span>
            </button>
            <div className="tw-relative">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-gray-400" />
              <input
                type="text"
                placeholder={t("singles_inferno.search_placeholder", "尋找你的心動對象 😮‍💨")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-w-full tw-rounded-full tw-border-0 tw-bg-white tw-py-2 tw-pl-10 tw-pr-4 tw-text-sm tw-shadow tw-transition-all focus:tw-ring-2 focus:tw-ring-[deepskyblue]"
              />
            </div>
          </div>
        </div>

        <FriendFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          newIdea={newIdea}
          setNewIdea={setNewIdea}
          handleSubmit={handleSubmit}
          loadingSubmit={loadingSubmit}
          handleImageChange={handleImageChange}
          handleRemoveImage={handleRemoveImage}
        />

        <AnimatePresence mode="wait" initial={false}>
          <ProfileDetailModal
            open={!!selectedIdea}
            selectedIdea={selectedIdea}
            onClose={() => setSelectedIdea(null)}
            handleVote={handleVote}
            votedIdeas={votedIdeas}
            showToast={showToast}
          />

          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}

          {/* <InfoToast minVotes={minVotes} /> */}
        </AnimatePresence>

        <div className="tw-space-y-4">
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                正在載入冒險...
              </motion.p>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="tw-py-12 tw-text-center"
            >
              <motion.div
                animate={{
                  y: [0, -10, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <MapPin className="tw-mx-auto tw-mb-4 tw-h-16 tw-w-16 tw-text-gray-300" />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mb-2 tw-text-xl tw-font-semibold tw-text-gray-900"
              >
                {t("singles_inferno.no_results", "未找到單身者")}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="tw-text-gray-500"
              >
                {t("singles_inferno.no_results_message", "嘗試調整您的搜索或推薦您的朋友！")}
              </motion.p>
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredIdeas.map((idea, index) => (
                <motion.div
                  layout="position"
                  key={idea.id}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -20 }}
                  transition={{
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.3 },
                    layout: { duration: 0.3 },
                    delay: index * 0.1,
                  }}
                  className="tw-overflow-hidden tw-rounded-xl tw-bg-white tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
                    <div className="tw-flex tw-items-center" onClick={() => setSelectedIdea(idea)}>
                      <div className="tw-mr-3 tw-h-16 tw-w-16 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full">
                        <img
                          src={idea.images[0]}
                          alt=""
                          className="tw-h-full tw-w-full tw-rounded-full tw-object-cover"
                        />
                      </div>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-lg tw-font-medium">{idea.name}</span>
                        <span className="tw-text-sm tw-text-gray-500">
                          {t("singles_inferno.posted_by", {
                            author: idea.author || "user",
                          })}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                          `${window.location.origin}/inferno?search=${idea.name}`
                        );
                        showToast("已複製個人資料連結！", "success");
                      }}
                      className="tw-flex tw-items-center tw-gap-1 tw-p-2 tw-text-gray-500 hover:tw-text-gray-700"
                    >
                      <span>{t("singles_inferno.share_profile", "分享資料")}</span>
                      <ArrowUpRight className="tw-h-5 tw-w-5" />
                    </button>
                  </div>

                  <div className="tw-p-3">
                    <p
                      className="tw-mb-2 tw-line-clamp-2 tw-text-gray-600"
                      onClick={() => setSelectedIdea(idea)}
                    >
                      {idea.description}
                    </p>
                    <div className="tw-my-4 tw-flex tw-items-center tw-justify-between">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        className="tw-flex tw-items-center tw-space-x-1"
                        onClick={() => handleVote(idea.id, true)}
                      >
                        <Heart
                          className={`tw-h-6 tw-w-6 ${
                            votedIdeas.has(idea.id)
                              ? "tw-fill-rose-500 tw-text-rose-500"
                              : "tw-text-rose-500"
                          }`}
                        />
                        <span className="tw-font-medium">
                          {idea.votes} {t("singles_inferno.vote_plural", "票")}
                        </span>
                      </motion.button>

                      <button
                        className="tw-font-medium tw-text-pink-500 hover:tw-text-blue-700"
                        onClick={() => setSelectedIdea(idea)}
                      >
                        {t("singles_inferno.view_more", "查看更多")}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SinglesInferno;
